"use client"

import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { WishlistButton } from "@/components/cells/WishlistButton/WishlistButton"
import { getProductPrice } from "@/lib/helpers/get-product-price"
import { useAppContext } from "@/lib/context/app-context"
import { HttpTypes } from "@medusajs/types"
import { useTranslate } from "@tolgee/react"
import Image from "next/image"
import { BarebonesAddToCartButton } from "./BarebonesAddToCartButton"

export const BarebonesProductCard = ({
  product,
  locale,
}: {
  product: HttpTypes.StoreProduct
  locale: string
}) => {
  const { t } = useTranslate()
  const { user, wishlist } = useAppContext()
  const { cheapestVariant, cheapestPrice } = getProductPrice({ product })
  const isAvailable = Boolean(
    cheapestVariant && cheapestPrice?.calculated_price
  )
  const onSale = Boolean(
    cheapestPrice &&
      typeof cheapestPrice.original_price_number === "number" &&
      typeof cheapestPrice.calculated_price_number === "number" &&
      cheapestPrice.original_price_number > cheapestPrice.calculated_price_number
  )

  return (
    <article className="bb-product-card group relative flex flex-col">
      <LocalizedClientLink
        href={`/products/${product.handle}`}
        className="relative block overflow-hidden bg-[#ece8e2]"
      >
        <div className="relative aspect-square overflow-hidden">
          {product.thumbnail ? (
            <Image
              src={decodeURIComponent(product.thumbnail)}
              alt={product.title || ""}
              fill
              className="bb-product-card-image object-contain p-6"
              sizes="(min-width: 1024px) 25vw, 50vw"
            />
          ) : null}
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2b2118]/20 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            aria-hidden
          />
        </div>
        {!isAvailable && (
          <span className="absolute left-3 top-3 rounded-sm bg-[#8a6a45] px-2 py-1 text-[11px] uppercase tracking-wide text-white">
            {t("product.sold_out", "Sold out")}
          </span>
        )}
        {isAvailable && onSale && (
          <span className="absolute left-3 top-3 rounded-sm bg-[#2b2118] px-2 py-1 text-[11px] uppercase tracking-wide text-[#f7f4ef]">
            {t("product.sale", "Sale")}
          </span>
        )}
        <span
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] origin-left scale-x-0 bg-[#2b2118] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100"
          aria-hidden
        />
      </LocalizedClientLink>

      <div className="absolute right-3 top-3 z-10">
        <WishlistButton
          product={product}
          productId={product.id}
          wishlist={wishlist}
          user={user}
          skin="barebones"
        />
      </div>

      <div className="mt-3 flex items-start justify-between gap-3">
        <LocalizedClientLink
          href={`/products/${product.handle}`}
          className="min-w-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-0.5"
        >
          <h3 className="truncate text-[15px] text-[#2b2118] transition-colors duration-300 group-hover:text-[#1a140f]">
            {product.title}
          </h3>
          {cheapestPrice?.calculated_price && (
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p
                className={`text-[14px] transition-colors duration-300 ${
                  onSale
                    ? "font-semibold text-[#8a3a2a]"
                    : "text-[#5c5148] group-hover:text-[#2b2118]"
                }`}
              >
                {cheapestPrice.calculated_price}
              </p>
              {onSale && (
                <p className="text-[13px] text-[#5c5148]/65 line-through">
                  {cheapestPrice.original_price}
                </p>
              )}
            </div>
          )}
        </LocalizedClientLink>
        <BarebonesAddToCartButton
          variant={cheapestVariant}
          title={product.title || ""}
          locale={locale}
          thumbnail={product.thumbnail}
          disabled={!isAvailable}
          className="bb-product-card-cta shrink-0"
        >
          {t("product.add_to_cart", "Add to cart")}
        </BarebonesAddToCartButton>
      </div>
    </article>
  )
}
